import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, formatDate, errMsg, todayStr, confirmDelete } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Stat, profitTone } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { TxPanel } from "@/components/TxPanel";
import { VEHICLE_FIELDS } from "@/pages/Vehicles";
import { ArrowLeft, Car, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get(`/vehicles/${id}`).then((r) => { setD(r.data); setTick((t) => t + 1); })
    .catch(() => navigate("/araclar")), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;
  const { vehicle: v, summary: s, sales } = d;
  const sale = sales[0];
  const w = can("otomotiv");

  const del = async (url, msg) => {
    try { if (await confirmDelete(url)) { toast.success(msg); load(); } } catch (e) { toast.error(errMsg(e)); }
  };
  const archiveExpense = (e) => del(`/vehicle-expenses/${e.id}`, "Masraf iptal edildi");

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/araclar")} className="gap-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Araçlar</Button>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Car className="w-6 h-6" /></div>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-2xl md:text-3xl" data-testid="vehicle-title">{v.plate} · {v.brand} {v.model}</h1>
            <div className="text-sm text-muted-foreground">{[v.year, v.vehicle_type, v.current_km != null && `${v.current_km.toLocaleString("tr-TR")} km`, v.chassis_number && `Şasi ${v.chassis_number}`].filter(Boolean).join(" · ")}</div>
          </div>
          <StatusBadge value={v.status} testId="vehicle-status" />
        </div>
        {w && (
          <div className="flex flex-wrap gap-2">
            <FormDialog title="Masraf Ekle" triggerLabel="Masraf" triggerTestId="add-expense-btn" url="/vehicle-expenses" onDone={load}
              successMsg="Masraf eklendi" initial={{ vehicle_id: id, expense_date: todayStr() }}
              fields={[{ name: "category_id", label: "Kategori", type: "entity", endpoint: "/categories", params: { business_area: "Otomotiv", type: "Gider" }, required: true },
                { name: "amount", label: "Tutar (₺)", type: "number", required: true }, { name: "expense_date", label: "Tarih", type: "date" },
                { name: "supplier_id", label: "Tedarikçi", type: "entity", endpoint: "/suppliers" },
                { name: "employee_id", label: "Personel", type: "entity", endpoint: "/employees" },
                { name: "receipt_number", label: "Fiş/Fatura No" }, { name: "description", label: "Açıklama", full: true },
                { name: "paid_now", label: "Ödendi olarak kaydet", type: "checkbox" }]} />
            <FormDialog title="Araç Hareketi" triggerLabel="Hareket" triggerTestId="add-movement-btn" url="/vehicle-movements" onDone={load}
              confirmKey="confirm_sold" successMsg="Hareket kaydedildi"
              initial={{ vehicle_id: id, departure_date: todayStr(), departure_km: v.current_km, movement_type: "Görev" }}
              fields={[{ name: "employee_id", label: "Personel", type: "entity", endpoint: "/employees" },
                { name: "person_name", label: "Kişi (personel değilse)" },
                { name: "movement_type", label: "Tür", type: "select", options: ["Görev", "Test Sürüşü", "Servis", "Teslimat", "Özel", "Diğer"] },
                { name: "purpose", label: "Amaç", required: true }, { name: "destination", label: "Gidilen Yer", full: true },
                { name: "departure_date", label: "Çıkış Tarihi", type: "date" }, { name: "departure_time", label: "Çıkış Saati", type: "time" },
                { name: "departure_km", label: "Çıkış Km", type: "number", required: true }, { name: "return_km", label: "Dönüş Km (döndüyse)", type: "number" },
                { name: "return_date", label: "Dönüş Tarihi", type: "date" }, { name: "return_time", label: "Dönüş Saati", type: "time" }]} />
            {!sale && (
              <FormDialog title="Araç Satışı" triggerLabel="Satış" triggerTestId="add-sale-btn" url="/vehicle-sales" onDone={load}
                successMsg="Satış kaydedildi" initial={{ vehicle_id: id, sale_date: todayStr(), received_amount: 0 }}
                fields={[{ name: "customer_id", label: "Müşteri", type: "entity", endpoint: "/customers", required: true },
                  { name: "salesperson_employee_id", label: "Satış Personeli", type: "entity", endpoint: "/employees" },
                  { name: "sale_price", label: "Satış Fiyatı (₺)", type: "number", required: true }, { name: "sale_date", label: "Satış Tarihi", type: "date" },
                  { name: "received_amount", label: "Peşin Alınan (₺)", type: "number" }, { name: "notes", label: "Not", type: "textarea" }]} />
            )}
            <FormDialog title="Ek Alış / Düzeltme" triggerLabel="Alış" triggerVariant="outline" triggerTestId="add-purchase-btn" url="/vehicle-purchases"
              onDone={load} successMsg="Alış kaydedildi" initial={{ vehicle_id: id, purchase_date: todayStr() }}
              fields={[{ name: "purchase_price", label: "Tutar (₺)", type: "number", required: true }, { name: "purchase_date", label: "Tarih", type: "date" },
                { name: "supplier_id", label: "Tedarikçi", type: "entity", endpoint: "/suppliers" }, { name: "notes", label: "Not" }]} />
            <FormDialog title="Aracı Düzenle" triggerLabel="Düzenle" triggerVariant="outline" icon={false} triggerTestId="edit-vehicle-btn"
              url={`/vehicles/${id}`} method="patch" onDone={load} successMsg="Araç güncellendi" initial={v}
              transform={(x) => Object.fromEntries(VEHICLE_FIELDS.map((f) => [f.name, x[f.name]]).concat([["notes", x.notes]]))}
              fields={v.status === "Satıldı" ? VEHICLE_FIELDS.filter((f) => f.name !== "status") : VEHICLE_FIELDS} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Stat label="Toplam Alış" value={money(s.total_purchase)} testId="sum-purchase" />
        <Stat label="Toplam Masraf" value={money(s.total_expenses)} testId="sum-expenses" />
        <Stat label="Toplam Maliyet" value={money(s.total_cost)} testId="sum-cost" />
        <Stat label="Satış" value={money(s.sale_price)} testId="sum-sale" />
        <Stat label="Kâr / Zarar" value={money(s.profit)} tone={profitTone(s.profit)} testId="sum-profit" />
        <Stat label="Hareket" value={s.movement_count} hint={`${s.movement_km} km`} testId="sum-movements" />
        <Stat label="Hafriyat Seferi" value={s.trip_count} hint={`${s.trip_km} km`} testId="sum-trips" />
        <Stat label="Toplam Km" value={s.total_km.toLocaleString("tr-TR")} hint={`Hafriyat gideri ${money(s.excavation_cost_total)}`} testId="sum-km" />
      </div>

      <Tabs defaultValue="expenses">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="expenses" data-testid="tab-expenses">Masraflar ({d.expenses.length})</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Hareketler ({d.movements.length})</TabsTrigger>
          <TabsTrigger value="trips" data-testid="tab-trips">Hafriyat Seferleri ({d.trips.length})</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-job-report">İş Raporu</TabsTrigger>
          <TabsTrigger value="trade" data-testid="tab-trade">Alış / Satış</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Finansal Hareketler</TabsTrigger>
        </TabsList>
        <TabsContent value="expenses"><Card className="p-4">
          <DataTable testIdPrefix="expense-row" rows={d.expenses} empty="Masraf kaydı yok" columns={[
            { key: "expense_date", label: "Tarih", render: (e) => formatDate(e.expense_date) },
            { key: "category_name", label: "Kategori" }, { key: "description", label: "Açıklama" },
            { key: "supplier_name", label: "Tedarikçi" }, { key: "employee_name", label: "Personel" },
            { key: "payment_status", label: "Ödeme", render: (e) => <StatusBadge value={e.payment_status} /> },
            { key: "amount", label: "Tutar", align: "right", render: (e) => <b>{money(e.amount)}</b> },
            { key: "x", label: "", render: (e) => w && <Button size="icon" variant="ghost" onClick={() => archiveExpense(e)} data-testid={`archive-expense-${e.id}`}><Trash2 className="w-4 h-4" /></Button> },
          ]} />
        </Card></TabsContent>
        <TabsContent value="movements"><Card className="p-4">
          <DataTable testIdPrefix="movement-row" rows={d.movements} empty="Hareket kaydı yok" columns={[
            { key: "departure_date", label: "Çıkış", render: (m) => `${formatDate(m.departure_date)} ${m.departure_time || ""}` },
            { key: "employee_name", label: "Kullanan", render: (m) => m.employee_name || m.person_name },
            { key: "movement_type", label: "Tür" }, { key: "purpose", label: "Amaç" }, { key: "destination", label: "Yer" },
            { key: "km", label: "Km", align: "right", render: (m) => `${m.departure_km} → ${m.return_km ?? "?"}` },
            { key: "distance_km", label: "Mesafe", align: "right", render: (m) => `${m.distance_km} km` },
            { key: "status", label: "Durum", render: (m) => <StatusBadge value={m.status} /> },
            { key: "r", label: "", render: (m) => w && m.status === "Dışarıda" && <ReturnBtn m={m} onDone={load} /> },
          ]} />
        </Card></TabsContent>
        <TabsContent value="trips"><Card className="p-4">
          <DataTable testIdPrefix="trip-row" rows={d.trips} empty="Hafriyat seferi yok" columns={[
            { key: "trip_date", label: "Tarih", render: (t) => `${formatDate(t.trip_date)} ${t.departure_time || ""}` },
            { key: "job_name", label: "İş" }, { key: "customer_name", label: "Müşteri" }, { key: "driver_name", label: "Şoför" },
            { key: "route", label: "Güzergah", render: (t) => `${t.loading_location || "-"} → ${t.unloading_location || "-"}` },
            { key: "quantity", label: "Miktar", align: "right", render: (t) => `${t.quantity} ${t.quantity_unit}` },
            { key: "distance_km", label: "Km", align: "right" }, { key: "trip_number", label: "Sefer #", align: "right" },
          ]} />
        </Card></TabsContent>
        <TabsContent value="jobs"><JobReport vid={id} d={d} /></TabsContent>
        <TabsContent value="trade"><Card className="p-4 space-y-6">
          <div><h3 className="font-display font-semibold mb-2">Alış Kayıtları</h3>
            <DataTable testIdPrefix="purchase-row" rows={d.purchases} empty="Alış kaydı yok" columns={[
              { key: "purchase_date", label: "Tarih", render: (p) => formatDate(p.purchase_date) }, { key: "supplier_name", label: "Satıcı" },
              { key: "payment_status", label: "Ödeme", render: (p) => <StatusBadge value={p.payment_status} /> },
              { key: "purchase_price", label: "Tutar", align: "right", render: (p) => <b>{money(p.purchase_price)}</b> }]} /></div>
          <div><h3 className="font-display font-semibold mb-2">Satış</h3>
            <DataTable testIdPrefix="sale-row" rows={sales} empty="Satış kaydı yok" columns={[
              { key: "sale_date", label: "Tarih", render: (x) => formatDate(x.sale_date) }, { key: "customer_name", label: "Müşteri" },
              { key: "salesperson_name", label: "Satış Personeli" },
              { key: "payment_status", label: "Tahsilat", render: (x) => <StatusBadge value={x.payment_status} /> },
              { key: "received_amount", label: "Alınan", align: "right", render: (x) => money(x.received_amount) },
              { key: "sale_price", label: "Satış Fiyatı", align: "right", render: (x) => <b>{money(x.sale_price)}</b> },
              { key: "c", label: "", render: (x) => w && <Button size="sm" variant="ghost" data-testid="cancel-sale-btn"
                onClick={() => del(`/vehicle-sales/${x.id}`, "Satış iptal edildi")}>İptal</Button> }]} /></div>
        </Card></TabsContent>
        <TabsContent value="finance"><Card className="p-4"><TxPanel params={{ vehicle_id: id }} reloadKey={tick} onChange={load} /></Card></TabsContent>
      </Tabs>
    </div>
  );
}

function ReturnBtn({ m, onDone }) {
  return (
    <FormDialog title="Araç Dönüşü" triggerLabel="Dönüş" triggerSize="sm" triggerVariant="outline" icon={false} triggerTestId={`return-${m.id}`}
      url={`/vehicle-movements/${m.id}/return`} method="patch" onDone={onDone} successMsg="Dönüş kaydedildi"
      initial={{ return_date: todayStr(), return_km: m.departure_km }}
      fields={[{ name: "return_km", label: "Dönüş Km", type: "number", required: true }, { name: "return_date", label: "Tarih", type: "date" },
        { name: "return_time", label: "Saat", type: "time" }]} />
  );
}

function JobReport({ vid, d }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/vehicles/${vid}/job-report`, { params: { start: start || undefined, end: end || undefined } }).then((r) => setRows(r.data));
  }, [vid, start, end, d]);
  return (
    <Card className="p-4 space-y-4" data-testid="job-report">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Özel tarih aralığı:</span>
        <Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="job-report-start" />
        <Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="job-report-end" />
      </div>
      <DataTable testIdPrefix="job-report-row" rows={rows.map((r) => ({ ...r, id: r.job_id }))} empty="Bu araç için hafriyat seferi yok" columns={[
        { key: "job_name", label: "İş", className: "font-medium" },
        { key: "daily", label: "Bugün", align: "right" }, { key: "weekly", label: "Bu Hafta", align: "right" },
        { key: "monthly", label: "Bu Ay", align: "right" }, { key: "yearly", label: "Bu Yıl", align: "right" },
        { key: "custom", label: "Özel Aralık", align: "right" }, { key: "total", label: "Toplam", align: "right", render: (r) => <b>{r.total}</b> },
        { key: "km", label: "Km", align: "right" }]} />
      <div className="grid md:grid-cols-2 gap-4">
        <div><h4 className="font-semibold text-sm mb-2">Gidilen Müşteriler</h4>
          {d.customers.map((c) => <div key={c.id} className="flex justify-between text-sm py-1 border-b border-border"><span>{c.name}</span><b>{c.count} sefer</b></div>)}</div>
        <div><h4 className="font-semibold text-sm mb-2">Aracı Kullanan Personel</h4>
          {d.employees.map((c) => <div key={c.id} className="flex justify-between text-sm py-1 border-b border-border"><span>{c.name}</span><b>{c.count} kullanım</b></div>)}</div>
      </div>
    </Card>
  );
}
